import { createSlice } from '@reduxjs/toolkit';
import { isUneligibleMoveInError } from 'components/direct-sales/quote/utils';
import type { NormalizedError } from 'errors/types';

export type MoveInDateRegister = {
  id: string | number;
  period?: string;
  digits?: string;
};

export type VerifyCallResponse = {
  fuelType: string;
  accountId: string;
  supplyStartDate: string;
  fuel: {
    electricity?: {
      meterPointId: string;
      registers: MoveInDateRegister[];
    };
    gas?: {
      meterPointId: string;
      registers: MoveInDateRegister[];
    };
  };
};

export type MoveInDateState = {
  isLoading: boolean;
  errors: NormalizedError[] | null;
  data: {
    moveInDate: string | null;
    supplyStartDate: string | null;
    verifyCallResponse: VerifyCallResponse | null;
  };
};

export const initialState: MoveInDateState = {
  isLoading: false,
  errors: null,
  data: {
    moveInDate: null,
    supplyStartDate: null,
    verifyCallResponse: null
  }
};

export const slice = createSlice({
  name: 'moveInDate',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.isLoading = initialState.isLoading;
      state.errors = initialState.errors;
      state.data = initialState.data;
    },
    setMoveInDate: (state, { payload }) => {
      state.data.moveInDate = payload;
    },
    setSupplyStartDate: (state, { payload }) => {
      state.data.supplyStartDate = payload;
    },
    setIsLoading: (state, { payload = true }: { payload?: boolean }) => {
      state.isLoading = payload;
    },
    setError: (state, { payload }) => {
      state.isLoading = initialState.isLoading;
      state.errors = payload;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = initialState.isLoading;
      state.data.verifyCallResponse = payload.data;
    }
  }
});

export const {
  setInitialState,
  setMoveInDate,
  setSupplyStartDate,
  setIsLoading,
  setError,
  setSuccess
} = slice.actions;

export const selectSlice = (state) => state[slice.name];
export const selectData = (state) => selectSlice(state).data;
export const selectErrors = (state) => selectSlice(state).errors;
export const selectErrorsToBeDisplayed = (state) => {
  const errors = selectErrors(state) || [];
  return errors.filter((error) => error && !isUneligibleMoveInError(error));
};

export const selectIsLoading = (state) => selectSlice(state).isLoading;
export const selectVerifyApiErrors = (state) => {
  const errors = selectErrors(state) || [];
  return errors.filter((error) => error && error.status === '500');
};
export const selectMoveInDate = (state) => selectData(state).moveInDate;
export const selectAccountId = (state) =>
  selectVerifyCallResponse(state)?.accountId;
export const selectFuelType = (state) =>
  selectVerifyCallResponse(state)?.fuelType;
export const selectSupplyStartDate = (state) =>
  selectData(state).supplyStartDate;
export const selectVerifyCallResponse = (state) =>
  selectData(state).verifyCallResponse;
export const selectElectricityRegisters = (state) =>
  selectVerifyCallResponse(state)?.fuel.electricity.registers;
export const selectGasRegisters = (state) =>
  selectVerifyCallResponse(state)?.fuel.gas.registers;
export const selectElectricityMeterPointId = (state) =>
  selectVerifyCallResponse(state)?.fuel.electricity.meterPointId;
export const selectGasMeterPointId = (state) =>
  selectVerifyCallResponse(state)?.fuel.gas.meterPointId;

export default slice.reducer;
